import { Platform, Pressable, StyleProp, TextStyle, View } from 'react-native'
import Icon, { IconName } from '@ui/elements/Icon'
import { colors } from '@ui/colors'
import Typography from '@ui/typography'
import cn from '@ui/utils/cn'
import { TypographyProps } from '@ui/typography/types'
import useAnimatedPress from '@ui/animated/useAnimatedPress'
import { interpolate, useAnimatedStyle } from 'react-native-reanimated'
import Gradient from '@ui/effects/Gradient'

export type Text =
  | string
  | {
      text: string
      style?: StyleProp<TextStyle>
    }
  | undefined

export type Texts = Text[]

const colorTextSecondary = colors.black25

export function TextItem({ value, index }: { value: Text; index: number }) {
  const empty = <Icon name="minus" color={colorTextSecondary} />

  if (!value) {
    return empty
  }

  if (typeof value === 'string') {
    return (
      <Typography
        {...textProps}
        color={index === 0 ? colors.text : colorTextSecondary}
        className={cn(textProps.className, 'text-right')}
      >
        {value}
      </Typography>
    )
  }

  return (
    <Typography
      {...textProps}
      style={value.style}
      className={cn(textProps.className, 'text-right')}
    >
      {value.text || empty}
    </Typography>
  )
}

export function BooleanItem({ value }: { value: boolean }) {
  return (
    <Icon
      name={value ? 'check' : 'x'}
      color={value ? colors.green[600] : colors.red[600]}
    />
  )
}

interface Props {
  icon?: IconName
  title?: string
  desc?: string
  texts?: Texts
  body?: React.ReactNode
  bool?: boolean
  last?: boolean
}

export default function NestedRow({
  icon,
  title,
  desc,
  texts,
  body,
  bool,
  last,
}: Props) {
  const { animation, onPressIn, onPressOut } = useAnimatedPress()

  const stylePressed = useAnimatedStyle(() => {
    return {
      opacity: interpolate(animation.value, [0, 1], [0, 1]),
    }
  })

  return (
    <Pressable
      onPressIn={onPressIn}
      onPressOut={onPressOut}
      className="cursor-default"
    >
      <Gradient
        colors={[colors.black5, colors.black5]}
        start={[0, 0.5]}
        end={[1, 0.5]}
        style={stylePressed}
      />
      <View
        className={cn(
          'flex flex-1 flex-row items-start justify-between gap-6 border-b border-black/10 px-8 py-6',
          last && 'border-b-0'
        )}
      >
        <View className="flex min-h-8 shrink-0 flex-row items-center justify-start gap-8">
          {icon && (
            <View className="flex h-8 w-8 items-center justify-center">
              <Icon name={icon} color={colors.text} />
            </View>
          )}
          {title && <Typography {...textProps}>{title}</Typography>}
        </View>

        <View className="flex min-h-8 min-w-0 flex-1 flex-row items-center justify-end gap-6">
          {desc && (
            <Typography
              variant={textProps.variant}
              color={colors.desc}
              className={cn(
                'flex min-w-0 shrink flex-row text-clip whitespace-pre-wrap text-balance text-right'
              )}
            >
              {desc}
            </Typography>
          )}
          {texts?.map((text, index) => (
            <View key={index} className="shrink-0 flex-row">
              <TextItem value={text} index={index} />
            </View>
          ))}
          {body}
          {bool !== undefined && (
            <View className="flex flex-row items-center justify-end gap-2">
              <BooleanItem value={bool} />
            </View>
          )}
        </View>
      </View>
    </Pressable>
  )
}

export const textProps: TypographyProps = {
  variant: Platform.select({
    default: 'SM',
    web: 'SM',
  }),
  className: 'font-text-semibold',
  color: colors.text,
}
